import type { HarvestFxPort } from "./effects.ts";
import { HarvestFx } from "./effects.ts";
import type { HarvestStore } from "./store.ts";

/**
 * What a map's harvest shows of the own character's hold on the canvas (ADR-142's amendment of 2026-10-31): the loop
 * starts and ends with the store's hold, and each blow shakes the prop and throws its leaves. Reduced motion keeps the
 * loop, which is the character's pose, and drops the shake and the leaves.
 */

/**
 * What the stage's effects read and ask of the page.
 */
export interface HarvestStageOptions {
  readonly store: Pick<HarvestStore, "subscribe" | "holding" | "heldMark">;
  readonly local: { harvesting(active: boolean, nowMs: number): void; readonly harvestFrame: number };
  readonly canvas: HarvestFxPort;
  readonly now: () => number;
  readonly reduced: () => boolean;
}

/**
 * The effects of one stage.
 */
export interface HarvestStage {
  step(): void;
  end(): void;
}

/**
 * Binds the effects to the store's hold and to the frames of the own character's loop.
 *
 * @param options What the effects read and ask of the page.
 * @returns The stage's effects: `step` once a frame after the own character is drawn, `end` when the stage ends.
 */
export function bindHarvestStage(options: HarvestStageOptions): HarvestStage {
  const fx = new HarvestFx(
    {
      shake: (id) => {
        if (!options.reduced()) {
          options.canvas.shake(id);
        }
      },
      leaves: (x, y, feetY) => {
        if (!options.reduced()) {
          options.canvas.leaves(x, y, feetY);
        }
      },
    },
    () => options.store.heldMark(),
  );
  const stop = options.store.subscribe(() => options.local.harvesting(options.store.holding, options.now()));
  return {
    step: () => fx.step(options.local.harvestFrame),
    end: () => {
      stop();
      options.local.harvesting(false, options.now());
    },
  };
}
